<template>
  <div id="review-section" ref="reviewSectionContainer" class="review-section-container">
    <div v-if="showSkeleton">
      <h2 class="h3 title small-margin-bottom-s large-margin-bottom-m" :class="{ h4: isMobile }">Ratings & Reviews</h2>
      <Skeleton height="588px" />
    </div>

    <div v-else class="review-section">
      <FadeOverlay
        :custom-class-name="'reviews-fade-overlay'"
        :fade-custom-styles="FADE_ATTRIBUTES"
        :show-fade="false"
        @fade-clicked="handleToggleIsCondensed"
      >
        <template #main-fade-content>
          <NoReviews v-if="noReviews" />
          <div v-else>
            <RatingAndReviews v-if="isStatisticsReady" :total-results="totalResults" />
            <SearchAndFilterBar :id="REVIEWS_SEARCH_ID" class="margin-bottom-m search-filter" />
            <div class="margin-bottom-s grid-x" aria-live="polite">
              <p id="reviewsHeader" class="h5 margin-right-xs" role="heading" aria-level="5" v-html="sanitize(reviewsHeader)"></p>
              <button v-if="isFiltered" class="link-body" @click="resetFilters">Clear</button>
            </div>
            <div
              v-for="(review, i) in alwaysShownReviews"
              :key="review.reviewId"
              class="review-comment-wrapper"
              :class="{
                'second-review': applySecondReviewAndFadeClass(i),
                'no-border': applyNoBorderClass(i),
              }"
            >
              <FadeOverlay
                :show-fade="applySecondReviewAndFadeClass(i)"
                :custom-class-name="'review-comment-fade'"
                :fade-custom-styles="FADE_ATTRIBUTES"
                @fade-clicked="handleToggleIsCondensed"
              >
                <template #main-fade-content>
                  <Review
                    :review="review"
                    :is-first-review="i === 0"
                    :is-first-review-focused="i === 0 && !isCondensed"
                    :is-comment-form-shown="openCommentId === review.reviewId"
                    @toggle-comment="handleToggleComment"
                    @on-select-photo="displayFullReview"
                  />
                </template>
              </FadeOverlay>
            </div>
            <div ref="firstReviewRef" class="margin-bottom-s" :style="firstReviewOverlayStyle">
              <Review
                v-for="review in displayingReviews"
                :key="review.reviewId"
                :review="review"
                :is-comment-form-shown="openCommentId === review.reviewId"
                @toggle-comment="handleToggleComment"
                @on-select-photo="displayFullReview"
              />
            </div>
            <ReviewsPagination v-if="!isCondensed" />
          </div>
          <button
            v-if="isToggleButtonShown"
            ref="reviewsToggle"
            class="button secondary reviews-toggle large-margin-top-xxs small-margin-top-m"
            @click="handleToggleIsCondensed"
          >
            {{ toggleBtnText }}
          </button>
        </template>
      </FadeOverlay>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, watch, onMounted, onBeforeMount, ref } from 'vue';
import { useIntersectionObserver } from '@vueuse/core';
import Skeleton from '@atomic-ui/skeleton/Skeleton';
import Review from '~/modules/reviews/components/IndividualGenericReview';
import NoReviews from '~/modules/reviews/components/NoReviews';
import SearchAndFilterBar from '~/modules/reviews/components/SearchAndFilterBar';
import ReviewsPagination from '~/modules/reviews/components/ReviewsPagination';
import FadeOverlay from '~/components/common/FadeOverlay';
import useReviewsStatistics from '~/modules/reviews/composables/useReviewsStatistics';
import useReviews from '~/modules/reviews/composables/useReviews';
import useReviewsPagination from '~/modules/reviews/composables/useReviewsPagination';
import useReviewsToggle from '~/modules/reviews/composables/useReviewsToggle';
import { useDeviceType } from '~/composables/utils/useDeviceType';
import { useModal } from '~/composables/utils/useModal';
import { useProduct } from '~/modules/product/composables/useProduct';
import useKillswitches from '~/modules/featureControl/composables/useKillswitches';
import { trackPageView, trackImpression } from '~/utils/bvPixel';
import RatingAndReviews from '~/modules/reviews/components/RatingAndReviews/RatingAndReviews';
import { REVIEWS_SEARCH_ID } from '../constants/reviews';
import { sanitize } from '@vue-util/sanitize';
import type { Review as ReviewType, Photo, Video } from '~~/shared/globalTypes/reviews';

const FADE_ATTRIBUTES = {
  background: 'linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.50) 28.13%, #FFF 88.02%)',
  'z-index': 1,
};

const { productId } = useProduct();

const { killswitches } = useKillswitches();
const { reviewVideoUploadEnabled = false } = killswitches;

const { reviews, starFiltering, sortBy, totalResults, searchQuery, fetchReviews, relatedProducts, resetFilters, reviewsHeader, isFiltered } =
  useReviews();

const { page } = useReviewsPagination();
const { open, initModalEntry, prefetchModal } = useModal();

const { isCondensable, isCondensed, toggleIsCondensed } = useReviewsToggle();
const { isReady: isStatisticsReady, totalCount } = useReviewsStatistics();
const noReviews = computed(() => Number(totalResults.value) === 0);

const handleToggleIsCondensed = () => {
  toggleIsCondensed();

  if (isCondensed.value) {
    reviewSectionContainer.value?.scrollIntoView({
      block: 'start',
      behavior: 'smooth',
    });
  }
};

const firstReviewRef = ref<HTMLElement | null>(null);

const reviewsToggle = ref<HTMLElement | null>(null);

const reviewSectionContainer = ref<HTMLElement | null>(null);

const aspectRatio = ref<number | null>(null);

const handleShadow = () => {
  if (reviewsToggle.value && firstReviewRef.value && reviewSectionContainer.value) {
    const shadowHeight = reviewsToggle.value.getBoundingClientRect().top - firstReviewRef.value.getBoundingClientRect().top;
    if (!Number.isNaN(shadowHeight) && shadowHeight > 0) {
      reviewSectionContainer.value.style.setProperty('--height', `${shadowHeight}px`);
    }
  }
};
const { isMobile, isTablet, isDesktopMedium, isDesktop } = useDeviceType();
const reviewsIsVisible = ref(false);
const isInitialized = ref(false);
const isInitializing = ref(false);

watch([isMobile, isTablet, isDesktopMedium, isDesktop, reviewsIsVisible, isInitialized], handleShadow);

const initialize = async () => {
  if (isInitialized.value || (isStatisticsReady.value && !totalCount.value)) return;
  isInitializing.value = true;
  isInitialized.value = true;
};

const { stop } = useIntersectionObserver(
  reviewSectionContainer,
  ([entry]) => {
    if (!entry?.isIntersecting) {
      return;
    }
    reviewsIsVisible.value = entry.isIntersecting;

    if (isInitialized.value && isInitializing.value) {
      stop();
      return;
    }

    initialize();
  },
  {
    rootMargin: '200px',
  },
);

onBeforeMount(() => {
  if (!noReviews.value) {
    prefetchModal('reviews');
  }
});

onMounted(() => {
  if (!noReviews.value) {
    const stats = relatedProducts.value[productId].reviewStatistics;
    // BV track for stats - these run once per page load
    trackPageView(stats, productId);
    trackImpression(reviews.value);
  }
});

const openCommentId = ref<number | null>(null);

watch([page, starFiltering, sortBy, searchQuery], () => {
  fetchReviews();
});

const showSkeleton = ref(false);

const toggleBtnText = computed(() => (isCondensed.value ? 'More reviews' : 'Hide Reviews'));
const isToggleButtonShown = computed(() => reviews.value.length > 1 && isCondensable.value);
const firstReviewOverlayStyle = computed(() =>
  isCondensed.value
    ? {
        height: 0,
        position: 'fixed',
      }
    : {},
);
const displayingReviews = computed(() => reviews.value.slice(2, reviews.value.length));
const alwaysShownReviews = computed(() => reviews.value.slice(0, 2));

// style classes conditionals
// 'no-border' conditional
const noBorderReview = computed(() => isCondensed.value && alwaysShownReviews.value.length > 0 && alwaysShownReviews.value.length <= 2);
// 'second-review' and 'review-comment-fade' conditional
const fadeSecondReviewAndFade = computed(() => isCondensed.value && alwaysShownReviews.value.length === 2);

/**
 * apply 'no-border' class if...
 * 1) noBorderReview conditional is met
 * 2) and is last review comment in alwaysShownReviews arr (1st or 2nd review comment)
 */
const applyNoBorderClass = (index: number): boolean => !!(noBorderReview.value && alwaysShownReviews.value.length - 1 === index);

/**
 * apply 'second-review' and 'review-comment-fade' class if...
 * 1) fadeSecondReviewAndFade conditional is met
 * 2) and is second review comment
 */
const applySecondReviewAndFadeClass = (index: number): boolean => !!(fadeSecondReviewAndFade.value && index === 1);

const handleToggleComment = (reviewId: number) => {
  if (openCommentId.value === reviewId) {
    openCommentId.value = null;
  } else {
    openCommentId.value = reviewId;
  }
};

const INDIVIDUAL_REVIEW_MODAL = 'individual-review-modal';
const selectedReview = ref<ReviewType[]>([]);
const clickedPhotoIndex = ref(0);

const parseMediaData = (media: Photo | Video, isVideo = false) => {
  if (isVideo) {
    const video = media as Video;
    return {
      id: video.videoId,
      url: video.url,
      caption: video.caption || '',
      type: 'video',
      thumbnailUrl: video.thumbnailUrl ?? '',
    };
  }

  const photo = media as Photo;
  return {
    id: photo.photoId,
    url: photo?.sizes?.normal?.url,
    caption: photo.caption || '',
    type: 'photo',
    thumbnailUrl: '',
  };
};

const photos = computed(() => {
  const review = selectedReview.value?.[0];
  if (!review) return [];

  const photosArr = (review.hasPhotos ? review.photos : []).map((media) => ({
    ...parseMediaData(media, false),
    reviewIndex: 0,
  }));
  const videosArr = ((reviewVideoUploadEnabled && review.videos) || []).map((media) => ({
    ...parseMediaData(media, true),
    reviewIndex: 0,
  }));

  return [...photosArr, ...videosArr];
});

initModalEntry({
  name: INDIVIDUAL_REVIEW_MODAL,
  props: computed(() => ({
    id: INDIVIDUAL_REVIEW_MODAL,
    photos: photos.value,
    modalName: INDIVIDUAL_REVIEW_MODAL,
    reviews: selectedReview.value,
    selectedIndex: clickedPhotoIndex.value,
    isLoading: false,
  })),
  aspectRatio: aspectRatio.value,
  componentName: 'reviews',
});

const displayFullReview = (review: ReviewType, selectedPhotoIndex: number, ratio: number | null) => {
  selectedReview.value = [review];
  aspectRatio.value = ratio;
  clickedPhotoIndex.value = selectedPhotoIndex;
  open(INDIVIDUAL_REVIEW_MODAL);
};
</script>

<style lang="scss" scoped>
@import '~settingSCSS';

.review-section-container {
  position: static;
  overflow: hidden;
  display: flex;
  flex-flow: column;
}

.review-section {
  display: flex;
  flex-flow: column;
}

.reviews-toggle {
  z-index: 1;
  width: 100%;
  max-width: rem-calc(335);
  align-self: center;
}

::v-deep(.reviews-fade-overlay) {
  height: var(--height, rem-calc(105));
  bottom: rem-calc(82);
  cursor: pointer;
}

::v-deep(.no-border .review-item) {
  border-bottom: none;
}

::v-deep(.review-comment-fade) {
  cursor: pointer;
  height: 12rem;
  bottom: -6rem;

  @include breakpoint(large up) {
    bottom: -4rem;
  }
}
.second-review {
  position: relative;
  height: 5rem;

  @include breakpoint(large up) {
    height: 2.5rem;
  }
}
// this was changed from h3 tag h2
// override is added to comply with previous spacing, that is overriden on Product.vue level
// when decided to remove override for h3 spacing on Product.vue - this can be deleted too
// There is TODO for that TD#1
.title {
  @include breakpoint(large up) {
    margin-bottom: rem-calc(16) !important;
  }
}

::v-deep(#reviewsHeader > span) {
  font-size: 0;
}
</style>
